import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "./api";

// Basit admin paneli (/admin). Tek parola ile giriş, yorum onaylama ve
// Instagram videosu içe aktarma. Tasarım sade tutuldu.
export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    apiFetch("/api/admin/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Yükleniyor...
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={() => setAuthed(false)} />
  ) : (
    <Login onSuccess={() => setAuthed(true)} />
  );
}

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await apiFetch("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      onSuccess();
    } catch (err) {
      setError(err.message || "Giriş başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm space-y-4"
      >
        <h1 className="text-2xl font-bold text-gray-800">Admin Girişi</h1>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Parola"
          className="w-full border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-400"
          autoFocus
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-sage-600 text-white py-2.5 rounded-xl disabled:opacity-60"
        >
          {busy ? "Giriş yapılıyor..." : "Giriş"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("pending");
  const [comments, setComments] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const data = await apiFetch(`/api/admin/comments?status=${status}`);
      setComments(data.comments || []);
      setCounts(data.counts || {});
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const act = async (id, status) => {
    await apiFetch(`/api/admin/comments/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    load(tab);
  };

  const save = async (id, changes) => {
    await apiFetch(`/api/admin/comments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    });
    load(tab);
  };

  const remove = async (id) => {
    if (!window.confirm("Bu yorum kalıcı olarak silinsin mi?")) return;
    await apiFetch(`/api/admin/comments/${id}`, { method: "DELETE" });
    load(tab);
  };

  const logout = async () => {
    await apiFetch("/api/admin/logout", { method: "POST" }).catch(() => {});
    onLogout();
  };

  const tabs = [
    ["pending", "Bekleyen"],
    ["approved", "Onaylı"],
    ["rejected", "Reddedilen"],
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800">
            Sahibim Tatilde — Yönetim
          </h1>
          <button
            onClick={logout}
            className="text-sm text-gray-500 hover:text-gray-800"
          >
            Çıkış
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        <ReelsImport />

        <section className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Yorumlar</h2>

          <div className="flex gap-2 mb-6">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2 rounded-full text-sm ${
                  tab === key
                    ? "bg-sage-600 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {label} {counts[key] ? `(${counts[key]})` : ""}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-gray-500">Yükleniyor...</p>
          ) : comments.length === 0 ? (
            <p className="text-gray-500">Kayıt yok.</p>
          ) : (
            <ul className="space-y-4">
              {comments.map((c) => (
                <CommentItem
                  key={c.id}
                  c={c}
                  tab={tab}
                  onAct={act}
                  onSave={save}
                  onRemove={remove}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function CommentItem({ c, tab, onAct, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(c);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startEdit = () => {
    setForm(c);
    setEditing(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await onSave(c.id, {
        name: form.name,
        location: form.location || "",
        pet: form.pet || "",
        rating: form.rating,
        comment: form.comment,
      });
      setEditing(false);
    } catch (err) {
      window.alert(err.message || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <li className="border rounded-xl p-4 space-y-3 bg-gray-50">
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            value={form.name}
            onChange={set("name")}
            placeholder="İsim"
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <div className="flex items-center gap-1 text-2xl">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                onClick={() => setForm((f) => ({ ...f, rating: n }))}
                className={n <= form.rating ? "text-yellow-400" : "text-gray-300"}
              >
                ★
              </button>
            ))}
          </div>
          <input
            value={form.location || ""}
            onChange={set("location")}
            placeholder="Bölge"
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            value={form.pet || ""}
            onChange={set("pet")}
            placeholder="Dostu"
            className="border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <textarea
          value={form.comment}
          onChange={set("comment")}
          rows={3}
          maxLength={1000}
          className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
        />
        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 rounded-lg bg-sage-600 text-white text-sm disabled:opacity-60"
          >
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-4 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-sm"
          >
            Vazgeç
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="border rounded-xl p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-gray-800">{c.name}</span>
          <span className="text-yellow-400">{"★".repeat(c.rating)}</span>
        </div>
        <p className="text-sm text-gray-500 mb-2">
          {[c.location, c.pet].filter(Boolean).join(" • ")}
        </p>
        <p className="text-gray-700">{c.comment}</p>
      </div>
      <div className="flex sm:flex-col gap-2 shrink-0">
        {tab !== "approved" && (
          <button
            onClick={() => onAct(c.id, "approved")}
            className="px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-sm"
          >
            Onayla
          </button>
        )}
        {tab !== "rejected" && (
          <button
            onClick={() => onAct(c.id, "rejected")}
            className="px-3 py-1.5 rounded-lg bg-yellow-100 text-yellow-700 text-sm"
          >
            Reddet
          </button>
        )}
        <button
          onClick={startEdit}
          className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 text-sm"
        >
          Düzenle
        </button>
        <button
          onClick={() => onRemove(c.id)}
          className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-sm"
        >
          Sil
        </button>
      </div>
    </li>
  );
}

function ReelsImport() {
  const [urls, setUrls] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const list = urls
      .split(/\s+/)
      .map((u) => u.trim())
      .filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const data = await apiFetch("/api/reels/admin/import", {
        method: "POST",
        body: JSON.stringify({ urls: list, force }),
      });
      setResult(data);
      setUrls("");
    } catch (err) {
      setError(err.data?.detail || err.message || "İçe aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-2">
        Instagram Videosu Ekle
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Gönderi linklerini boşluk/satırla ayırarak yapıştırın. Sunucu videoları
        indirip siteye ekler (biraz sürebilir).
      </p>
      <form onSubmit={submit} className="space-y-3">
        <textarea
          rows={3}
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          placeholder="https://www.instagram.com/p/XXXX/&#10;https://www.instagram.com/p/YYYY/"
          className="w-full border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-400 resize-none"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            className="bg-sage-600 text-white px-5 py-2.5 rounded-xl disabled:opacity-60"
          >
            {busy ? "İndiriliyor..." : "İçe Aktar"}
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
            />
            Yeniden indir (bozuk/güncel video için)
          </label>
        </div>
      </form>
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      {result && (
        <p className="text-sm text-green-700 mt-3">
          {result.count} video eklendi: {result.imported?.join(", ")}
        </p>
      )}
    </section>
  );
}
